import './index.scss';

import { Component, ReactNode, createRef } from 'react';

import { BraxButton, BraxButtonVariant } from '../BraxButton';
import { BraxList, BraxListItem, BraxListProps } from '../BraxList';

export type BraxDropdownProps = Partial<Pick<BraxListProps, 'listItemProps'>> & {
    open?: boolean,
    items: BraxListItem[],
    disabled: boolean,
    readOnly: boolean,
    position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'auto-left' | 'auto-right' | 'top-auto' | 'bottom-auto' | 'auto-auto',
    variant: BraxButtonVariant
    noButton: boolean,
    children: ReactNode,
    onToggle?: (open: boolean) => void
};

type BraxDropdownState = {
    active: boolean,
    positionX: 'left' | 'right',
    positionY: 'top' | 'bottom',
    items: BraxListItem[]
};

export class BraxDropdown extends Component<BraxDropdownProps, BraxDropdownState> {

    static defaultProps = {
        disabled: false,
        readOnly: false,
        position: 'auto-left',
        variant: 'tertiary',
        noButton: false
    };

    state: BraxDropdownState = {
        active: false,
        positionX: 'left',
        positionY: 'bottom',
        items: []
    };

    targetRef = createRef<HTMLElement>();

    dropdownRef = createRef<HTMLDivElement>();

    mounted = false;

    toggleButtonClicked = false;

    componentDidMount() {
        this.mounted = true;

        window.addEventListener('click', this.deactivateDropdown);

        this.updatePosition();

        this.updateOptions();
    }

    componentDidUpdate(prevProps: BraxDropdownProps) {
        if (
            prevProps.open !== this.props.open && !this.props.open ||
            prevProps.disabled !== this.props.disabled && this.props.disabled ||
            prevProps.readOnly !== this.props.readOnly && this.props.readOnly
        ) {
            this.toggleActive(false);

            this.toggleButtonClicked = false;
        }

        if (prevProps.position !== this.props.position)
            this.updatePosition();

        if (prevProps.items !== this.props.items)
            this.updateOptions();
    }

    componentWillUnmount() {
        this.mounted = false;

        window.removeEventListener('click', this.deactivateDropdown);
    }

    updateOptions() {
        this.setState({
            items: this.props.items.map(item => ({ ...item, onSelect: () => this.select(item) }))
        });
    }

    select(item: BraxListItem) {
        if (this.props.disabled || this.props.readOnly)
            return;

        item.onSelect?.();

        this.deactivateDropdown();
    }

    deactivateDropdown = () => {
        if (!this.mounted || this.props.disabled || this.props.readOnly || typeof this.props.open === 'boolean')
            return ;

        if (this.toggleButtonClicked === false)
            this.toggleActive(false);
        else
            this.toggleButtonClicked = false;
    }

    toggleDropdown() {
        if (!this.mounted || this.props.disabled || this.props.readOnly || typeof this.props.open === 'boolean')
            return ;

        this.toggleButtonClicked = true;

        if (!this.state.active)
            this.updatePosition();

        this.toggleActive();
    }

    updatePosition() {
        let [y, x] = this.props.position.split('-') as ['top' | 'bottom' | 'auto', 'left' | 'right' | 'auto'];

        let domRect: DOMRect | undefined;

        if (y === 'auto') {
            domRect = this.targetRef.current?.getBoundingClientRect();

            const containerDOMRect = this.getRefContainer(this.targetRef.current, 'y').getBoundingClientRect();

            y = domRect && this.dropdownRef.current && domRect.top - containerDOMRect.top >= this.dropdownRef.current.clientHeight ? 'top' : 'bottom';
        }

        if (x === 'auto') {
            domRect ||= this.targetRef.current?.getBoundingClientRect();

            const containerDOMRect = this.getRefContainer(this.targetRef.current, 'x').getBoundingClientRect();

            x = domRect && this.dropdownRef.current && domRect.left - containerDOMRect.left >= this.dropdownRef.current.clientWidth ? 'right' : 'left';
        }

        this.setState({ positionX: x, positionY: y });
    }

    getRefContainer(ref = this.targetRef.current, axis: 'x' | 'y'): HTMLElement {
        if (!ref)
            return document.documentElement;

        const overflow = window.getComputedStyle(ref)[`overflow${axis.toUpperCase()}`];

        return overflow !== 'visible' && overflow !== 'overlay' ? ref : this.getRefContainer(ref.parentElement, axis);
    }

    toggleActive(active?: boolean) {
        const value = active !== undefined ? active : !this.state.active;

        if (value === this.state.active)
            return;

        this.props.onToggle?.(value);

        this.setState({ active: value });
    }

    render() {
        const classStatus = this.props.disabled
            ? 'disabled'
        : this.props.readOnly
            ? 'read-only'
        : this.props.open || this.state.active
            ? 'active'
            : '';

        return (
            <div className={`brax-dropdown ${this.state.positionX} ${this.state.positionY} ${classStatus}`}>
                { this.props.noButton ? (
                    <div ref={this.targetRef as any} onClick={() => this.toggleDropdown()}>
                        { this.props.children }
                    </div>
                ) : (
                    <BraxButton innerRef={this.targetRef as any} variant={this.props.variant} onClick={() => this.toggleDropdown()}>
                        { this.props.children }
                    </BraxButton>
                ) }

                <div ref={this.dropdownRef}>
                    <BraxList items={this.state.items} listItemProps={this.props.listItemProps} />
                </div>
            </div>
        );
    }

}
